<template>
    <section class="node-allocation-page">
        <header class="allocation-header">
            <div>
                <div class="allocation-eyebrow">{{ admin ? 'Infrastructure · Administration' : 'Resources · ' + poolLabel }}</div>
                <h1>Node allocation</h1>
                <p>Recorded allocation by worker, nodepool, and class.</p>
            </div>
        </header>

        <v-card flat outlined class="allocation-controls">
            <section aria-label="Allocation controls">
                <div class="allocation-control-row allocation-all-controls">
                    <div class="allocation-scope-group">
                        <v-select
                            v-if="scopeRpid !== null"
                            v-model="sessionSourceSid"
                            class="allocation-space-input"
                            :items="spaceScopeOptions"
                            item-text="space_long_id"
                            item-value="sid"
                            label="Space scope"
                            placeholder="All spaces"
                            outlined
                            dense
                            hide-details
                            clearable
                            @change="setSessionSource" />
                        <v-btn
                            icon
                            class="allocation-source-refresh"
                            :loading="loading"
                            :disabled="!liveQuery"
                            aria-label="Refresh allocation sources"
                            title="Refresh allocation for this scope and window"
                            @click="refreshSources">
                            <v-icon>mdi-refresh</v-icon>
                        </v-btn>
                    </div>
                    <div class="allocation-time-group">
                        <v-menu v-model="windowMenu" class="allocation-window-menu" :close-on-content-click="false" transition="scale-transition" offset-y>
                            <template #activator="{ on, attrs }">
                                <v-text-field
                                    :value="windowSummary"
                                    class="allocation-window-input"
                                    label="Loaded window (UTC)"
                                    append-icon="mdi-calendar-range"
                                    readonly
                                    outlined
                                    dense
                                    hide-details
                                    :title="windowLabel"
                                    :aria-label="'Loaded window (UTC): ' + windowLabel"
                                    v-bind="attrs"
                                    v-on="on" />
                            </template>
                            <v-card class="allocation-window-picker">
                                <div class="allocation-window-presets" role="group" aria-label="Quick loaded windows">
                                    <v-btn
                                        v-for="period in periods"
                                        :key="period.hours"
                                        small
                                        text
                                        :color="periodHours === period.hours ? 'primary' : undefined"
                                        @click="choosePeriod(period.hours)">
                                        {{ period.label }}
                                    </v-btn>
                                </div>
                                <v-date-picker v-model="draftDates" range no-title />
                                <div class="allocation-window-times">
                                    <v-text-field v-model="draftStartTime" type="time" step="60" label="From (UTC)" outlined dense hide-details />
                                    <v-text-field v-model="draftEndTime" type="time" step="60" label="Until (UTC)" outlined dense hide-details />
                                </div>
                                <v-card-actions>
                                    <v-spacer />
                                    <v-btn text @click="windowMenu = false">Cancel</v-btn>
                                    <v-btn text color="primary" :disabled="!draftWindowValid" @click="applyDraftWindow">Apply window</v-btn>
                                </v-card-actions>
                            </v-card>
                        </v-menu>
                        <div class="allocation-window-nav">
                            <v-btn icon :disabled="!validWindow" aria-label="Previous window" @click="shiftWindow(-1)"><v-icon>mdi-chevron-left</v-icon></v-btn>
                            <v-btn icon :disabled="!validWindow || windowEnd >= latestEnd()" aria-label="Next window" @click="shiftWindow(1)">
                                <v-icon>mdi-chevron-right</v-icon>
                            </v-btn>
                            <v-btn small text color="primary" @click="resetEnd">Now</v-btn>
                        </div>
                    </div>
                    <v-menu offset-y class="allocation-sort-menu">
                        <template #activator="{ on, attrs }">
                            <v-btn icon :title="'Sort: ' + selectedSortLabel" :aria-label="'Sort: ' + selectedSortLabel" v-bind="attrs" v-on="on">
                                <v-icon>mdi-sort-variant</v-icon>
                            </v-btn>
                        </template>
                        <v-list dense>
                            <v-list-item
                                v-for="option in sortOptions"
                                :key="option.value"
                                :aria-current="sort === option.value ? 'true' : null"
                                @click="sort = option.value">
                                <v-list-item-title>{{ option.text }}</v-list-item-title>
                                <v-icon v-if="sort === option.value" small>mdi-check</v-icon>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                    <v-text-field
                        v-model="search"
                        class="allocation-search-input"
                        prepend-inner-icon="mdi-magnify"
                        label="Find a worker, pool or class"
                        outlined
                        dense
                        hide-details
                        clearable />
                    <v-select
                        v-if="admin"
                        v-model="cluster"
                        class="allocation-cluster-input"
                        :items="clusterOptions"
                        label="Cluster"
                        placeholder="All clusters"
                        outlined
                        dense
                        hide-details
                        clearable />
                    <ResourcePoolSelector
                        v-if="admin"
                        v-model="adminPool"
                        class="allocation-pool-input"
                        :items="resourcePools"
                        item-text="long_id"
                        item-value="rpid"
                        label="Resource pool"
                        placeholder="All resource pools"
                        outlined
                        dense
                        hide-details
                        clearable />
                </div>
            </section>
        </v-card>

        <div class="allocation-loading-slot"><v-progress-linear v-show="loading" indeterminate color="primary" aria-label="Loading allocation" /></div>
        <v-alert v-if="liveError" type="error" text dense class="mt-4">{{ liveError }}</v-alert>

        <v-alert v-if="!validWindow" type="error" text dense class="mt-4">Choose a valid window with its end after its start.</v-alert>
        <v-alert v-else-if="!admin && scopeRpid === null" type="info" text dense class="mt-4">Select a resource pool from the Resources menu.</v-alert>
        <template v-else>
            <div class="allocation-view-toolbar">
                <v-btn-toggle :value="viewMode" mandatory dense aria-label="Allocation view">
                    <v-btn value="pools" text @click="setView('pools')">Node pools</v-btn>
                    <v-btn value="nodes" text @click="setView('nodes')">Nodes</v-btn>
                </v-btn-toggle>
                <v-btn v-if="viewMode === 'pools' && focusedPool" small text color="primary" class="allocation-exit-pool-focus" @click="focusedPoolKey = null">
                    Show filtered nodepools
                </v-btn>
                <span class="allocation-results-summary">{{ allocation.nodes.length }} recorded workers · {{ poolAllocation.pools.length }} node pools</span>
            </div>
            <v-alert v-if="focusedNodeKey && !focusedNode" dense text type="info">
                The selected worker is outside the loaded window.
                <v-btn small text color="primary" @click="backToPool">Back to {{ focusedPool ? focusedPool.label : 'nodepool' }}</v-btn>
            </v-alert>

            <NodeAllocationBillingBreakdown v-if="poolBilling" :billing="poolBilling" />

            <div class="allocation-family-summary" role="group" aria-label="Resource category">
                <button
                    v-for="family in families"
                    :key="family.id"
                    type="button"
                    :class="['allocation-family', { 'allocation-family--active': familyFilter === family.id }]"
                    :aria-pressed="String(familyFilter === family.id)"
                    @click="chooseFamily(family.id)">
                    <v-icon color="primary">{{ family.icon }}</v-icon>
                    <span>
                        <strong>{{ family.label }}</strong>
                    </span>
                    <b>
                        {{ familyCount(family.id) }}
                        <small>{{ viewMode === 'pools' ? 'pools' : 'nodes' }}</small>
                    </b>
                </button>
            </div>

            <div v-if="allocation.nodes.length" class="allocation-legend">
                <span>
                    <i class="allocation-request-key"></i>
                    Reserved
                </span>
                <span>
                    <i class="allocation-limit-key"></i>
                    Limit
                </span>
                <span>
                    <i class="allocation-capacity-key"></i>
                    Loaded-worker capacity
                </span>
                <v-tooltip bottom>
                    <template #activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on" tabindex="0">
                            ≥
                            <v-icon small>mdi-information-outline</v-icon>
                        </span>
                    </template>
                    <span>Known minimum; total could be higher. Shading requires complete bounds. These are allocations, not measured utilization.</span>
                </v-tooltip>
            </div>

            <section v-for="group in groups" :key="group.id" class="allocation-group">
                <header class="allocation-group-heading">
                    <h2>{{ group.label }}</h2>
                    <span>{{ group.items.length }} {{ viewMode === 'pools' ? 'node pools' : 'nodes' }}</span>
                </header>
                <div class="allocation-nodes">
                    <template v-for="pool in viewMode === 'pools' ? group.items : []">
                        <NodeAllocationPoolCard
                            v-if="graphEntry('pool', pool)"
                            :key="pool.key"
                            :pool="graphEntry('pool', pool)"
                            :start="graphWindow('pool', pool).start"
                            :end="graphWindow('pool', pool).end"
                            :pinned-at="graphPinnedAt('pool', pool)"
                            :zoom-can-undo="graphCanUndo('pool', pool)"
                            :zoom-can-reset="graphCanReset('pool', pool)"
                            :selected-space="spaceSelections[pool.key] || null"
                            :initially-expanded="pool.key === focusedPoolKey"
                            @select-space="selectSpace(pool, $event)"
                            @inspect-node="inspectNode(pool, $event)"
                            @zoom="zoomGraph('pool', pool, $event)"
                            @undo-zoom="undoGraph('pool', pool)"
                            @reset-zoom="resetGraph('pool', pool)"
                            @pin="pinGraph('pool', pool, $event)" />
                        <div v-else :key="pool.key + '-empty'" class="allocation-graph-outside">
                            <span>No recorded pool activity in this graph interval.</span>
                            <v-btn small text color="primary" @click="undoGraph('pool', pool)">Undo zoom</v-btn>
                            <v-btn small text color="primary" @click="resetGraph('pool', pool)">Reset graph</v-btn>
                        </div>
                    </template>
                    <template v-for="entry in viewMode === 'nodes' ? group.items : []">
                        <NodeAllocationCard
                            v-if="graphEntry('node', entry)"
                            :key="nodeKey(entry)"
                            :allocation="graphEntry('node', entry)"
                            :start="graphWindow('node', entry).start"
                            :end="graphWindow('node', entry).end"
                            :admin="admin"
                            :pinned-at="graphPinnedAt('node', entry)"
                            :zoom-can-undo="graphCanUndo('node', entry)"
                            :zoom-can-reset="graphCanReset('node', entry)"
                            :nodepool-label="entry.poolLabel"
                            :from-pool="focusedNodeKey === nodeKey(entry)"
                            :initially-expanded="nodeKey(entry) === focusedNodeKey"
                            :resource-pools="resourcePools"
                            :node-pools="snapshot.node_pools"
                            @view-nodepool="showNodepool(entry)"
                            @zoom="zoomGraph('node', entry, $event)"
                            @undo-zoom="undoGraph('node', entry)"
                            @reset-zoom="resetGraph('node', entry)"
                            @pin="pinGraph('node', entry, $event)" />
                        <div v-else :key="nodeKey(entry) + '-empty'" class="allocation-graph-outside">
                            <span>Nodepool: {{ entry.poolLabel }}</span>
                            <v-btn small text color="primary" @click="showNodepool(entry)">
                                {{ focusedNodeKey === nodeKey(entry) ? 'Back to nodepool' : 'View nodepool' }}
                            </v-btn>
                            <span>No recorded worker activity in this graph interval.</span>
                            <v-btn small text color="primary" @click="undoGraph('node', entry)">Undo zoom</v-btn>
                            <v-btn small text color="primary" @click="resetGraph('node', entry)">Reset graph</v-btn>
                        </div>
                    </template>
                </div>
            </section>

            <v-card v-if="!hasEntries" flat outlined class="allocation-empty">
                <v-icon size="36" color="secondary">mdi-server-off</v-icon>
                <h2>{{ emptyHeading }}</h2>
            </v-card>
            <v-card v-else-if="!groups.length && !focusedNodeKey" flat outlined class="allocation-empty">
                <h2>No {{ activeFamily.label }} {{ viewMode === 'pools' ? 'pools' : 'nodes' }} match these filters</h2>
                <v-btn text color="primary" @click="clearFilters">Clear filters</v-btn>
            </v-card>

            <v-expansion-panels v-if="categoryUnplaced.length" flat class="allocation-unplaced">
                <v-expansion-panel>
                    <v-expansion-panel-header>
                        {{ unplacedHeading }}
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                        <p class="allocation-note">{{ unplacedNote }}</p>
                        <div v-for="session in categoryUnplaced" :key="session.session_id" class="allocation-unplaced-row">
                            <strong>{{ session.image_name || 'Application ' + session.aid }}</strong>
                            <span>{{ session.worker_node_name || 'Worker not recorded' }}</span>
                            <small>{{ timestamp(session.start_time) }} → {{ session.stop_time ? timestamp(session.stop_time) : 'Running' }}</small>
                            <v-btn
                                v-if="viewMode === 'nodes' && unplacedPoolBySession.has(session)"
                                small
                                text
                                color="primary"
                                class="allocation-unplaced-pool-link"
                                @click="focusPool(unplacedPoolBySession.get(session))">
                                Inspect in nodepool
                            </v-btn>
                        </div>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
        </template>
        <v-expansion-panels v-if="loadedScopeKey === liveScopeKey && liveResult.sources.length" flat class="allocation-backend-sources">
            <v-expansion-panel>
                <v-expansion-panel-header>Data sources · {{ liveResult.sessionCount }} records</v-expansion-panel-header>
                <v-expansion-panel-content>
                    <v-alert v-if="liveResult.limitations.length" type="info" text dense class="mt-4">
                        <div v-for="limitation in liveResult.limitations" :key="limitation">{{ limitation }}</div>
                    </v-alert>
                    <div class="allocation-source-table-scroll">
                        <table class="allocation-source-table">
                            <thead>
                                <tr>
                                    <th>Source</th>
                                    <th>Endpoint</th>
                                    <th>Status</th>
                                    <th>Records / detail</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="source in liveResult.sources" :key="source.key">
                                    <td>{{ source.label }}</td>
                                    <td>
                                        <code>{{ source.url || 'Not available' }}</code>
                                    </td>
                                    <td>{{ source.status }}</td>
                                    <td>
                                        {{ source.count === null ? '—' : source.count }}
                                        <small>{{ source.message }}</small>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <p class="allocation-note">Capacity covers loaded workers; allocation reflects the selected billing scope.</p>
                    <div v-if="snapshot.node_history.length" class="allocation-source-table-scroll">
                        <table class="allocation-source-table">
                            <thead>
                                <tr>
                                    <th>Recorded worker</th>
                                    <th>Node pool</th>
                                    <th>CPU (Kubernetes quantity)</th>
                                    <th>RAM (Kubernetes quantity)</th>
                                    <th>Local ephemeral storage (Kubernetes quantity)</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="node in snapshot.node_history" :key="node.nhid">
                                    <td>{{ node.node_name }}</td>
                                    <td>{{ node.node_pool || 'Not recorded' }}</td>
                                    <td>
                                        Allocatable: {{ nodeQuantity(node, 'allocatable', 'cpu') }}
                                        <small>Hardware: {{ nodeQuantity(node, 'capacity', 'cpu') }}</small>
                                    </td>
                                    <td>
                                        Allocatable: {{ nodeQuantity(node, 'allocatable', 'memory') }}
                                        <small>Hardware: {{ nodeQuantity(node, 'capacity', 'memory') }}</small>
                                    </td>
                                    <td>
                                        Allocatable: {{ nodeQuantity(node, 'allocatable', 'ephemeral-storage') }}
                                        <small>Filesystem capacity: {{ nodeQuantity(node, 'capacity', 'ephemeral-storage') }}</small>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div v-if="snapshot.sessions.length" class="allocation-source-table-scroll">
                        <table class="allocation-source-table">
                            <thead>
                                <tr>
                                    <th>Retrieved application</th>
                                    <th>Recorded worker</th>
                                    <th>Billed pool</th>
                                    <th>Session lifetime (UTC)</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="session in snapshot.sessions" :key="session.session_id">
                                    <td>{{ session.image_name || 'Application ' + session.aid }}</td>
                                    <td>{{ session.worker_node_name || 'Not recorded' }}</td>
                                    <td>{{ session.rpid === undefined || session.rpid === null ? 'Not exposed' : session.rpid }}</td>
                                    <td>{{ timestamp(session.start_time) }} → {{ session.stop_time ? timestamp(session.stop_time) : 'Open' }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </v-expansion-panel-content>
            </v-expansion-panel>
        </v-expansion-panels>
    </section>
</template>

<script>
import { mapState } from 'vuex'
import NodeAllocationCard from './NodeAllocationCard.vue'
import NodeAllocationPoolCard from './NodeAllocationPoolCard.vue'
import NodeAllocationBillingBreakdown from './NodeAllocationBillingBreakdown.vue'
import ResourcePoolSelector from './ResourcePoolSelector.vue'
import { nodePoolWindow, recordedTime, formatAllocationTime, resourcePoolBilling } from './nodeAllocationData'
import { loadNodeAllocationSources } from './nodeAllocationApi'

const inputTime = value => new Date(value).toISOString().slice(0, 16)
const emptySnapshot = () => ({ sessions: [], node_history: [], node_pools: [], tenant_clusters: [], resource_pools: [], spaces: [] })
const emptyLiveResult = () => ({ snapshot: emptySnapshot(), sources: [], limitations: [], spaces: [], sessionsAvailable: false, sessionCount: 0 })
const coversWindow = (entry, start, end) =>
    Boolean(entry && Number.isFinite(start) && Number.isFinite(end) && start < end && entry.start <= start && entry.end >= end)

export default {
    name: 'NodeAllocationPage',
    components: { NodeAllocationCard, NodeAllocationPoolCard, NodeAllocationBillingBreakdown, ResourcePoolSelector },
    props: { admin: { type: Boolean, default: false } },
    data() {
        return {
            liveResult: emptyLiveResult(),
            cachedSources: {},
            loadedScopeKey: null,
            loadedCoverage: null,
            loading: false,
            liveError: '',
            loadGeneration: 0,
            sessionSourceSid: this.$route.params.sid ? Number(this.$route.params.sid) : null,
            sessionSourcePoolId: this.$route.params.rpid ? Number(this.$route.params.rpid) : null,
            periodHours: 6,
            periods: [
                { label: '1h', hours: 1 },
                { label: '3h', hours: 3 },
                { label: '6h', hours: 6 },
                { label: '1d', hours: 24 },
                { label: '3d', hours: 72 },
                { label: '7d', hours: 168 }
            ],
            endInput: inputTime(Date.now()),
            customStartInput: inputTime(Date.now() - 6 * 3600000),
            windowMenu: false,
            draftDates: [],
            draftStartTime: '',
            draftEndTime: '',
            graphWindows: {},
            graphPins: {},
            viewMode: 'pools',
            focusedPoolKey: null,
            focusedNodeKey: null,
            spaceSelections: {},
            search: '',
            familyFilter: 'standard',
            adminPool: null,
            cluster: null,
            sort: 'name',
            sortOptions: [
                { text: 'Name', value: 'name' },
                { text: 'CPU recorded-budget density', value: 'cpu' },
                { text: 'RAM recorded-budget density', value: 'memory' }
            ],
            families: [
                { id: 'standard', label: 'Standard', icon: 'mdi-server' },
                { id: 'elastic', label: 'Elastic', icon: 'mdi-cloud-outline' },
                { id: 'integrations', label: 'Integrations', icon: 'mdi-link' }
            ]
        }
    },
    computed: {
        ...mapState('userStore', ['selectedRP', 'availableResourcePools']),
        scopeRpid() {
            const id = Number(this.admin ? this.adminPool : this.$route.params.rpid)
            return Number.isInteger(id) && id > 0 ? id : null
        },
        includeDescendants() {
            return this.scopeRpid !== null
        },
        allocationScopeFilter() {
            return this.includeDescendants ? null : this.scopeRpid
        },
        liveScopeKey() {
            if (!this.admin && this.scopeRpid === null) return null
            const sid = this.sessionSourcePoolId === this.scopeRpid ? Number(this.sessionSourceSid) : null
            return JSON.stringify({
                admin: this.admin,
                rpid: this.scopeRpid,
                sid: Number.isInteger(sid) && sid > 0 ? sid : null,
                descendants: this.includeDescendants
            })
        },
        snapshot() {
            return this.loadedScopeKey === this.liveScopeKey ? this.liveResult.snapshot : emptySnapshot()
        },
        spaceScopeOptions() {
            if (this.scopeRpid === null) return []
            const unfilteredKey = JSON.stringify({ admin: this.admin, rpid: this.scopeRpid, sid: null, descendants: this.includeDescendants })
            const unfiltered = (this.cachedSources[unfilteredKey] || []).find(entry => coversWindow(entry, this.windowStart, this.windowEnd))
            const byId = new Map((unfiltered?.result.snapshot.spaces || []).map(space => [Number(space.sid), space]))
            for (const space of this.snapshot.spaces) byId.set(Number(space.sid), space)
            return [...byId.values()]
        },
        resourcePools() {
            const byId = new Map(this.availableResourcePools.map(pool => [Number(pool.rpid), pool]))
            for (const pool of this.snapshot.resource_pools) byId.set(Number(pool.rpid), pool)
            return [...byId.values()]
        },
        poolLabel() {
            return (
                this.resourcePools.find(pool => Number(pool.rpid) === this.scopeRpid)?.long_id ||
                (Number(this.selectedRP?.rpid) === this.scopeRpid ? this.selectedRP.long_id : null) ||
                `Resource pool ${this.scopeRpid === null ? 'not selected' : this.scopeRpid}`
            )
        },
        activeFamily() {
            return this.families.find(family => family.id === this.familyFilter)
        },
        selectedSortLabel() {
            return this.sortOptions.find(option => option.value === this.sort)?.text || 'Name'
        },
        windowEnd() {
            return recordedTime(this.endInput)
        },
        windowStart() {
            return this.periodHours === 0 ? recordedTime(this.customStartInput) : this.windowEnd - this.periodHours * 3600000
        },
        validWindow() {
            return Number.isFinite(this.windowStart) && Number.isFinite(this.windowEnd) && this.windowStart < this.windowEnd
        },
        windowLabel() {
            if (!this.validWindow) return 'Choose dates and times'
            return `${inputTime(this.windowStart).replace('T', ' ')} – ${inputTime(this.windowEnd).replace('T', ' ')}`
        },
        // Compact cover for the ~200px activator; windowLabel keeps the full
        // minute-precision range for the title and accessible name.
        windowSummary() {
            if (!this.validWindow) return 'Choose dates and times'
            const short = value => {
                const date = new Date(value)
                const day = `${String(date.getUTCMonth() + 1).padStart(2, '0')}-${String(date.getUTCDate()).padStart(2, '0')}`
                const time = `${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}`
                return `${day} ${time}`
            }
            return `${short(this.windowStart)} – ${short(this.windowEnd)}`
        },
        draftWindow() {
            if (
                this.draftDates.length !== 2 ||
                !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(this.draftStartTime) ||
                !/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(this.draftEndTime)
            )
                return null
            const dates = [...this.draftDates].sort()
            const start = recordedTime(`${dates[0]}T${this.draftStartTime}`)
            const end = recordedTime(`${dates[1]}T${this.draftEndTime}`)
            return Number.isFinite(start) && Number.isFinite(end) && start < end ? { start, end } : null
        },
        draftWindowValid() {
            return this.draftWindow !== null
        },
        activeCoverage() {
            return this.loadedScopeKey === this.liveScopeKey ? this.loadedCoverage : null
        },
        liveQuery() {
            if (!this.liveScopeKey || !this.validWindow) return null
            const scope = JSON.parse(this.liveScopeKey)
            return { rpid: scope.rpid, sid: scope.sid, start_time: new Date(this.windowStart).toISOString(), end_time: new Date(this.windowEnd).toISOString() }
        },
        liveQueryKey() {
            return JSON.stringify(this.liveQuery)
        },
        poolAllocation() {
            if (!this.validWindow || !this.activeCoverage) return { pools: [], unplaced: [] }
            const start = Math.max(this.windowStart, this.activeCoverage.start)
            const end = Math.min(this.windowEnd, this.activeCoverage.end)
            return nodePoolWindow(this.snapshot, start, end, this.allocationScopeFilter)
        },
        poolBilling() {
            if (!this.includeDescendants || !this.activeCoverage || !this.validWindow) return null
            const start = Math.max(this.windowStart, this.activeCoverage.start)
            const end = Math.min(this.windowEnd, this.activeCoverage.end)
            return resourcePoolBilling(this.snapshot, start, end, this.scopeRpid)
        },
        zoomedGraphs() {
            const byWindow = new Map()
            const views = new Map()
            for (const [key, window] of Object.entries(this.graphWindows)) {
                if (!this.activeCoverage || !coversWindow(this.activeCoverage, window.start, window.end)) continue
                const range = `${window.start}:${window.end}`
                let model = byWindow.get(range)
                if (!model) {
                    model = nodePoolWindow(this.snapshot, window.start, window.end, this.allocationScopeFilter)
                    byWindow.set(range, model)
                }
                views.set(key, model)
            }
            return views
        },
        allocation() {
            return { nodes: this.poolAllocation.pools.flatMap(pool => pool.nodes), unplaced: this.poolAllocation.unplaced }
        },
        focusedNode() {
            return this.focusedNodeKey ? this.allocation.nodes.find(entry => this.nodeKey(entry) === this.focusedNodeKey) || null : null
        },
        focusedPool() {
            return this.poolAllocation.pools.find(pool => pool.key === this.focusedPoolKey) || null
        },
        spaceLabels() {
            return new Map((this.snapshot.spaces || []).map(space => [String(space.sid), space.space_long_id]))
        },
        unplacedPoolBySession() {
            const bySession = new Map()
            for (const pool of this.poolAllocation.pools) for (const session of pool.unplaced) bySession.set(session, pool)
            return bySession
        },
        categoryUnplaced() {
            if (this.viewMode === 'pools' && this.focusedPool) return []
            // Pool cards already present attributable sessions; keep the rest
            // here without inventing a nodepool association.
            const sessions = this.allocation.unplaced.filter(
                session => this.sessionFamily(session) === this.familyFilter && (this.viewMode === 'nodes' || !this.unplacedPoolBySession.has(session))
            )
            return sessions.length < 2
                ? sessions
                : sessions.sort(
                      (left, right) =>
                          recordedTime(left.start_time) - recordedTime(right.start_time) || String(left.session_id).localeCompare(String(right.session_id))
                  )
        },
        unplacedHeading() {
            const count = this.categoryUnplaced.length
            return this.viewMode === 'pools'
                ? `${count} ${this.activeFamily.label} sessions without a nodepool association`
                : `${count} ${this.activeFamily.label} sessions with unresolved worker placement`
        },
        unplacedNote() {
            return this.viewMode === 'pools'
                ? 'These records match no displayed nodepool, so they stay unplotted here instead of inventing a worker or pool association.'
                : 'These records have unresolved worker placement. Sessions with a known nodepool can be inspected in its graph; others have no safe graph association.'
        },
        hasEntries() {
            return this.viewMode === 'pools' ? this.poolAllocation.pools.length > 0 : this.allocation.nodes.length > 0
        },
        emptyHeading() {
            if (this.loading) return 'Loading allocation'
            if (this.liveError) return 'Allocation could not be loaded'
            return this.viewMode === 'pools' ? 'No recorded node pools in this window' : 'No recorded workers in this window'
        },
        clusterOptions() {
            return [...new Set(this.poolAllocation.pools.map(pool => pool.clusterName || 'Platform cluster'))].sort()
        },
        groups() {
            const query = (this.search || '').trim().toLowerCase()
            const source = this.viewMode === 'pools' ? this.poolAllocation.pools : this.allocation.nodes
            if (this.viewMode === 'nodes' && this.focusedNodeKey) return this.focusedNode ? [{ ...this.activeFamily, items: [this.focusedNode] }] : []
            if (this.viewMode === 'pools' && this.focusedPool)
                return [{ ...this.families.find(family => family.id === this.focusedPool.family), items: [this.focusedPool] }]
            return this.families
                .filter(family => family.id === this.familyFilter)
                .map(family => ({
                    ...family,
                    items: source
                        .filter(entry => {
                            if (entry.family !== family.id || (this.cluster && (entry.clusterName || 'Platform cluster') !== this.cluster)) return false
                            if (!query) return true
                            const names =
                                this.viewMode === 'pools'
                                    ? [entry.label, ...entry.spaces.map(space => space.label), ...entry.nodes.map(node => node.node.node_name)]
                                    : [
                                          entry.node.node_name,
                                          entry.poolLabel,
                                          ...entry.sessions.flatMap(session => [session.image_name, session.instance_long_id, this.spaceName(session.sid)])
                                      ]
                            return [...names, entry.clusterName, ...entry.poolNames].some(value =>
                                String(value || '')
                                    .toLowerCase()
                                    .includes(query)
                            )
                        })
                        .sort((left, right) => {
                            if (this.sort !== 'name') {
                                const density = entry => {
                                    const metric = entry.resources.find(resource => resource.key === this.sort)
                                    return metric?.capacity > 0 && metric.limit !== null ? metric.limit / metric.capacity : -1
                                }
                                const difference = density(right) - density(left)
                                if (difference) return difference
                            }
                            return (this.viewMode === 'pools' ? left.label : left.node.node_name).localeCompare(
                                this.viewMode === 'pools' ? right.label : right.node.node_name
                            )
                        })
                }))
                .filter(group => group.items.length)
        }
    },
    methods: {
        timestamp: formatAllocationTime,
        nodeQuantity(node, kind, key) {
            return node.meta_data?.[kind]?.[key] ?? 'Not recorded'
        },
        graphId(kind, entry) {
            return kind === 'pool' ? `pool:${entry.key}` : `node:${this.nodeKey(entry)}`
        },
        graphWindow(kind, entry) {
            return this.graphWindows[this.graphId(kind, entry)] || { start: this.windowStart, end: this.windowEnd, history: [] }
        },
        graphEntry(kind, entry) {
            const model = this.zoomedGraphs.get(this.graphId(kind, entry))
            if (!model) return entry
            return kind === 'pool'
                ? model.pools.find(pool => pool.key === entry.key) || null
                : model.pools.flatMap(pool => pool.nodes).find(node => this.nodeKey(node) === this.nodeKey(entry)) || null
        },
        graphCanUndo(kind, entry) {
            return this.graphWindow(kind, entry).history.length > 0
        },
        graphCanReset(kind, entry) {
            return Boolean(this.graphWindows[this.graphId(kind, entry)])
        },
        graphPinnedAt(kind, entry) {
            return this.graphPins[this.graphId(kind, entry)] ?? null
        },
        pinGraph(kind, entry, at) {
            const key = this.graphId(kind, entry)
            if (at === null) this.$delete(this.graphPins, key)
            else this.$set(this.graphPins, key, at)
        },
        zoomGraph(kind, entry, window) {
            const current = this.graphWindow(kind, entry)
            const start = Math.max(current.start, window?.start)
            const end = Math.min(current.end, window?.end)
            if (!Number.isFinite(start) || !Number.isFinite(end) || start >= end || (start === current.start && end === current.end)) return
            const key = this.graphId(kind, entry)
            this.$set(this.graphWindows, key, {
                start,
                end,
                history: [...current.history, { start: current.start, end: current.end }]
            })
            const pin = this.graphPins[key]
            if (pin !== undefined && (pin < start || pin >= end)) this.$delete(this.graphPins, key)
        },
        undoGraph(kind, entry) {
            const key = this.graphId(kind, entry)
            const current = this.graphWindows[key]
            if (!current?.history.length) return
            const history = current.history.slice(0, -1)
            const previous = current.history[current.history.length - 1]
            if (!history.length) this.$delete(this.graphWindows, key)
            else this.$set(this.graphWindows, key, { ...previous, history })
        },
        resetGraph(kind, entry) {
            this.$delete(this.graphWindows, this.graphId(kind, entry))
        },
        clearGraphWindows() {
            this.graphWindows = {}
            this.graphPins = {}
        },
        nodeKey(entry) {
            return JSON.stringify([
                entry.node.tcid ?? null,
                entry.node.nhid ?? entry.node.node_uid ?? entry.node.node_name,
                recordedTime(entry.node.start_time)
            ])
        },
        spaceName(sid) {
            return sid === null || sid === undefined ? 'Space not recorded' : this.spaceLabels.get(String(sid)) || `Space ${sid}`
        },
        latestEnd() {
            return recordedTime(inputTime(Date.now()))
        },
        resetEnd() {
            this.endInput = inputTime(this.latestEnd())
        },
        setPeriod(hours) {
            this.periodHours = hours
        },
        choosePeriod(hours) {
            this.setPeriod(hours)
            this.windowMenu = false
        },
        prepareWindowDraft() {
            if (!this.validWindow) return
            const start = inputTime(this.windowStart)
            const end = inputTime(this.windowEnd)
            this.draftDates = [start.slice(0, 10), end.slice(0, 10)]
            this.draftStartTime = start.slice(11)
            this.draftEndTime = end.slice(11)
        },
        applyDraftWindow() {
            if (!this.draftWindow) return
            this.applyWindow(this.draftWindow.start, this.draftWindow.end)
            this.windowMenu = false
        },
        applyWindow(start, end) {
            if (!Number.isFinite(start) || !Number.isFinite(end) || start >= end) return
            const from = inputTime(start)
            const until = inputTime(end)
            if (from >= until) return
            this.periodHours = 0
            this.customStartInput = from
            this.endInput = until
            this.clearGraphWindows()
        },
        setSessionSource(value) {
            this.sessionSourceSid = value ? Number(value) : null
            this.sessionSourcePoolId = this.scopeRpid
        },
        useCachedSources(entry, key) {
            this.liveResult = entry.result
            this.loadedCoverage = { start: entry.start, end: entry.end }
            this.loadedScopeKey = key
        },
        async loadSources(force = false) {
            const generation = ++this.loadGeneration
            const key = this.liveScopeKey
            const query = this.liveQuery
            this.liveError = ''
            if (!query) {
                this.loading = false
                return
            }
            const entries = this.cachedSources[key] || []
            const cached = entries.find(entry => coversWindow(entry, this.windowStart, this.windowEnd))
            if (!force && cached) {
                this.useCachedSources(cached, key)
                this.loading = false
                return
            }
            const start = force && coversWindow(this.activeCoverage, this.windowStart, this.windowEnd) ? this.activeCoverage.start : this.windowStart
            const end = force && coversWindow(this.activeCoverage, this.windowStart, this.windowEnd) ? this.activeCoverage.end : this.windowEnd
            const request = { ...query, start_time: new Date(start).toISOString(), end_time: new Date(end).toISOString() }
            this.loading = true
            try {
                const result = await loadNodeAllocationSources(this.$axios, request, {
                    admin: this.admin,
                    sid: request.sid,
                    includeDescendants: this.includeDescendants
                })
                if (this._isBeingDestroyed || this._isDestroyed) return
                const entry = { result, start, end, generation }
                const previous = this.cachedSources[key] || []
                const newerSameWindow = previous.some(old => old.start === start && old.end === end && old.generation > generation)
                if (!newerSameWindow) {
                    const retained = previous.filter(old => old.start !== start || old.end !== end)
                    this.$set(
                        this.cachedSources,
                        key,
                        [entry, ...retained].sort((a, b) => b.generation - a.generation)
                    )
                }
                if (this.liveScopeKey === key) {
                    const usable = this.cachedSources[key].find(old => coversWindow(old, this.windowStart, this.windowEnd))
                    if (usable) this.useCachedSources(usable, key)
                }
            } catch (error) {
                if (generation === this.loadGeneration && key === this.liveScopeKey)
                    this.liveError =
                        error.response?.data?.debug ||
                        error.response?.data?.description ||
                        error.response?.data?.message ||
                        error.message ||
                        'Could not load allocation.'
            } finally {
                if (generation === this.loadGeneration) this.loading = false
            }
        },
        refreshSources() {
            return this.loadSources(true)
        },
        setView(mode) {
            this.viewMode = mode
            this.focusedNodeKey = null
            this.focusedPoolKey = null
        },
        inspectNode(pool, entry) {
            this.focusedPoolKey = pool.key
            this.focusedNodeKey = this.nodeKey(entry)
            const fromPool = this.graphWindows[this.graphId('pool', pool)]
            const nodeKey = this.graphId('node', entry)
            if (fromPool && !this.graphWindows[nodeKey]) this.$set(this.graphWindows, nodeKey, { ...fromPool, history: [...fromPool.history] })
            this.viewMode = 'nodes'
        },
        focusPool(pool) {
            if (!pool) return
            this.focusedPoolKey = pool.key
            this.focusedNodeKey = null
            this.familyFilter = pool.family
            this.viewMode = 'pools'
        },
        showNodepool(entry) {
            this.focusPool(this.poolAllocation.pools.find(candidate => candidate.key === entry.poolKey))
        },
        backToPool() {
            if (this.focusedNode) this.showNodepool(this.focusedNode)
            else {
                if (this.focusedPool) this.familyFilter = this.focusedPool.family
                this.focusedNodeKey = null
                this.viewMode = 'pools'
            }
        },
        chooseFamily(id) {
            this.familyFilter = id
            if (this.viewMode === 'pools') this.focusedPoolKey = null
        },
        selectSpace(pool, space) {
            this.$set(this.spaceSelections, pool.key, space ? { key: space.key, sid: space.sid, label: space.label } : null)
        },
        familyCount(id) {
            return (this.viewMode === 'pools' ? this.poolAllocation.pools : this.allocation.nodes).filter(entry => entry.family === id).length
        },
        sessionFamily(session) {
            if (session.tcid !== null && session.tcid !== undefined) return 'integrations'
            if (session.active_resource === 'ncu' || !session.node_pool || session.node_pool === 'nv-app') return 'standard'
            const pool = this.snapshot.node_pools.find(pool => pool.short_id === session.node_pool || `nv-app${pool.short_id}` === session.node_pool)
            return pool?.cloud === 'azure' ? 'elastic' : 'integrations'
        },
        shiftWindow(direction) {
            const shift = direction * (this.windowEnd - this.windowStart)
            this.applyWindow(this.windowStart + shift, this.windowEnd + shift)
        },
        clearFilters() {
            this.search = ''
            this.familyFilter = 'standard'
            this.cluster = null
            this.focusedPoolKey = null
            this.focusedNodeKey = null
        },
        clearNavigation() {
            this.clearGraphWindows()
            this.focusedPoolKey = null
            this.focusedNodeKey = null
            this.spaceSelections = {}
        }
    },
    watch: {
        windowMenu(open) {
            if (open) this.prepareWindowDraft()
        },
        liveQueryKey: {
            handler() {
                this.clearGraphWindows()
                this.loadSources()
            },
            immediate: true
        },
        liveScopeKey() {
            this.clearNavigation()
        },
        scopeRpid() {
            this.setSessionSource(this.$route.params.sid)
            this.clearFilters()
        },
        '$route.params.sid'(value) {
            this.setSessionSource(value)
        }
    },
    beforeDestroy() {
        this.loadGeneration++
    }
}
</script>

<style scoped>
.allocation-graph-outside {
    display: flex;
    gap: 8px;
    align-items: center;
    flex-wrap: wrap;
    padding: 14px;
    border: 1px solid #e0e7ef;
    border-radius: 8px;
    background: white;
    font-size: 12px;
    color: #64748b;
}
.allocation-loading-slot {
    height: 4px;
}
.allocation-view-toolbar {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
    margin: 12px 0;
}
.node-allocation-page {
    width: 100%;
    padding: 24px;
    color: #243653;
}
.allocation-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 20px;
}
.allocation-eyebrow {
    color: #64748b;
    font-size: 12px;
    margin-bottom: 6px;
}
.allocation-header h1 {
    font-size: 26px;
    font-weight: 600;
    margin: 0;
}
.allocation-header p {
    color: #64748b;
    margin: 6px 0 0;
    font-size: 14px;
}
.allocation-controls {
    padding: 20px;
    border-radius: 12px;
}
.allocation-control-row {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
}
.allocation-all-controls > .allocation-scope-group {
    display: flex;
    align-items: center;
    gap: 4px;
    flex: 0 0 calc(20% - 6px);
    min-width: 190px;
}
.allocation-scope-group > .allocation-space-input {
    flex: 1 1 auto;
    min-width: 0;
}
.allocation-source-refresh {
    flex: 0 0 36px;
}
.allocation-all-controls > .allocation-time-group {
    display: flex;
    align-items: center;
    gap: 2px;
    flex: 0 0 calc(25% - 6px);
    min-width: 260px;
}
.allocation-time-group > .allocation-window-menu {
    flex: 1 1 0;
    min-width: 0;
}
.allocation-window-input {
    width: 100%;
}
.allocation-all-controls > .allocation-sort-menu {
    display: flex;
    justify-content: center;
    flex: 0 0 5%;
    min-width: 48px;
}
.allocation-all-controls > .allocation-search-input {
    flex: 1 1 0;
    min-width: 180px;
}
.allocation-all-controls > .allocation-cluster-input,
.allocation-all-controls > .allocation-pool-input {
    flex: 0 1 160px;
    min-width: 145px;
}
.allocation-window-nav {
    display: flex;
    align-items: center;
    flex: 0 0 auto;
}
.allocation-window-nav .v-btn--icon {
    width: 28px;
    height: 32px;
    min-width: 28px;
}
.allocation-window-nav .v-btn:not(.v-btn--icon) {
    min-width: 0;
    padding: 0 4px;
}
.allocation-window-picker {
    width: 320px;
    max-width: calc(100vw - 24px);
}
.allocation-window-presets {
    display: flex;
    flex-wrap: wrap;
    gap: 2px;
    padding: 8px;
    border-bottom: 1px solid #e0e7ef;
}
.allocation-window-times {
    display: flex;
    gap: 8px;
    padding: 8px 12px;
}
.allocation-window-times > .v-input {
    min-width: 0;
    flex: 1;
}
.allocation-backend-sources {
    margin-top: 16px;
}
.allocation-source-table-scroll {
    overflow-x: auto;
    margin: 12px 0;
}
.allocation-source-table {
    border-collapse: collapse;
    width: 100%;
    text-align: left;
    font-size: 11px;
}
.allocation-source-table th {
    font-weight: 500;
    color: #64748b;
}
.allocation-source-table td,
.allocation-source-table th {
    padding: 10px;
    border-bottom: 1px solid #e0e7ef;
}
.allocation-source-table small {
    display: block;
    margin-top: 4px;
    color: #64748b;
}
.allocation-results-summary {
    color: #64748b;
    font-size: 12px;
}
.allocation-family-summary {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 12px;
}
.allocation-family {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 16px;
    text-align: left;
    background: white;
    border: 1px solid #e0e7ef;
    border-radius: 8px;
    color: inherit;
}
.allocation-family:hover,
.allocation-family--active {
    border-color: #3381ff;
    background: #f5f9ff;
}
.allocation-family strong {
    font-size: 14px;
}
.allocation-family small {
    display: block;
    font-size: 11px;
    font-weight: 400;
    color: #64748b;
    margin-top: 3px;
}
.allocation-family b {
    margin-left: auto;
    text-align: right;
    font-size: 22px;
}
.allocation-legend {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px 24px;
    font-size: 11px;
    color: #64748b;
    margin: 20px 0;
}
.allocation-legend span {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}
.allocation-legend i {
    display: inline-block;
    width: 18px;
    height: 7px;
}
.allocation-request-key {
    border-top: 2px solid #64748b;
}
.allocation-limit-key {
    border-top: 2px dashed #64748b;
}
.allocation-capacity-key {
    border-top: 2px dashed #8391a5;
}
.allocation-legend .allocation-legend-note {
    margin-left: auto;
}
.allocation-group {
    margin: 24px 0;
}
.allocation-group-heading {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 12px;
}
.allocation-group-heading h2 {
    font-size: 17px;
    font-weight: 600;
    margin: 0;
}
.allocation-group-heading span {
    color: #64748b;
    font-size: 12px;
}
.allocation-nodes {
    display: grid;
    gap: 16px;
}
.allocation-empty {
    margin-top: 24px;
    padding: 40px 20px;
    text-align: center;
}
.allocation-empty h2 {
    font-size: 18px;
    margin: 12px 0 8px;
}
.allocation-empty p,
.allocation-note {
    color: #64748b;
    font-size: 12px;
    line-height: 1.7;
}
.allocation-unplaced {
    margin: 20px 0;
}
.allocation-unplaced-row {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
    gap: 12px;
    font-size: 12px;
    padding: 10px 0;
    border-top: 1px solid #e0e7ef;
}
@media (max-width: 900px) {
    .allocation-family-summary {
        grid-template-columns: 1fr;
    }
    .allocation-legend .allocation-legend-note {
        margin-left: 0;
    }
}
@media (max-width: 600px) {
    .node-allocation-page {
        padding: 16px 8px;
    }
    .allocation-header h1 {
        font-size: 22px;
    }
    .allocation-controls {
        padding: 12px;
    }
    .allocation-all-controls > .allocation-scope-group,
    .allocation-all-controls > .allocation-time-group {
        flex: 1 1 100%;
        min-width: 0;
    }
    .allocation-all-controls > .allocation-sort-menu {
        flex: 0 0 48px;
    }
    .allocation-all-controls > .allocation-search-input {
        flex: 1 1 calc(100% - 56px);
        min-width: 0;
    }
    .allocation-all-controls > .allocation-cluster-input,
    .allocation-all-controls > .allocation-pool-input {
        flex: 1 1 100%;
        min-width: 0;
    }
}
</style>
